/**
 * Shared cell-display helpers for Reports and Panel tables. Report/pivot
 * rows carry raw dataset values (an ISO calendar date, or a full ISO
 * instant like a Dexie `updatedAt`) — both rendered verbatim looked like
 * `2026-08-17T19:39:54.666Z` in the preview table, which is unreadable
 * at a glance. Detect the shape read-side (dataset column definitions
 * don't currently carry a type hint) and format it as a locale date for
 * display only; the underlying row value is never mutated.
 */
import { defaultDayTimeZone, formatFarmDateTime } from './farmTime'

const ISO_DATE_OR_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?Z?)?$/

export const isIsoDateLikeString = (value: unknown): value is string => (
  typeof value === 'string' && ISO_DATE_OR_DATETIME_PATTERN.test(value.trim())
)

export const formatIsoDateLikeForDisplay = (value: string, timeZone?: string): string => {
  const trimmed = value.trim()
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return value
  // A calendar day — a bare date (findings #82) or the UTC-midnight sentinel
  // web tables store (findings #85) — is shown as that day; an instant as its
  // day in the farm's zone, not the viewer's browser zone.
  return formatFarmDateTime(trimmed, timeZone ?? defaultDayTimeZone(), { dateOnly: true })
}

/**
 * Many dataset row fields (`feedKg`, `weight`, `fishStock`, …) are stored
 * as decimal *strings*, not JS numbers — a plain `typeof value ===
 * 'number'` check misses them entirely for right-alignment purposes.
 * `Number(trimmed)` (unlike `parseFloat`) requires the WHOLE string to be
 * numeric, so a label like "Lot 1234" or a date/UUID string correctly
 * stays excluded.
 */
export const isNumericLikeValue = (value: unknown): boolean => {
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  return Number.isFinite(Number(trimmed))
}

/**
 * `isNumericLikeValue` judges a single cell in isolation, which misreads
 * identifier columns whose values happen to look numeric — a lot whose key
 * is "104" gets right-aligned like a measurement, while "LOT-1" in the same
 * column stays left-aligned, so the column visibly zigzags. Columns are
 * identified by their dataset key, not sniffed from content, since a
 * column's *kind* (identifier vs. measurement) doesn't change row to row —
 * `lotKey`/`farmId`/`lotId`/`id`/`title`/`name` are always labels.
 */
const IDENTIFIER_COLUMN_KEY_PATTERN = /(^id$|Id$|^lotKey$|Key$|^title$|^name$|Name$)/

export const isIdentifierLikeColumnKey = (key: string): boolean => IDENTIFIER_COLUMN_KEY_PATTERN.test(key)

/**
 * A numeric-looking identifier (a lot key of "104") must not right-align
 * just because a sibling row's lot key happens to be "LOT-1" and reads as
 * text — see `isIdentifierLikeColumnKey`'s docstring. Shared by Reports.tsx
 * and Dashboard.tsx's pinned-report preview so both render the same cell
 * the same way.
 */
export const cellTextAlign = (columnKey: string, value: unknown): 'left' | 'right' => (
  isNumericLikeValue(value) && !isIdentifierLikeColumnKey(columnKey) ? 'right' : 'left'
)

export const stringifyCell = (value: unknown, unserializableLabel: string): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return isIsoDateLikeString(value) ? formatIsoDateLikeForDisplay(value) : value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    const json = JSON.stringify(value)
    return typeof json === 'string' ? json : ''
  } catch {
    return unserializableLabel
  }
}

/**
 * When a farm is removed, its lots' historical FeedLog/production rows
 * stay in the local cache and keep surfacing in reports — several
 * datasets fall back to the row's raw stored `lot`/`lotKey` label when it
 * can't resolve against the *current* lot roster (e.g.
 * `resolveDatasetLotReference` in reportDatasets.ts), and `productionDatatable`
 * passes stored entities through unresolved by design. Left as-is, a
 * lot label like "101" is visually indistinguishable from a real, current
 * lot — this appends a clear suffix instead, keyed off whether the row's
 * `farmId` is still in the operator's current farm roster (the one signal
 * every affected dataset's rows carry, regardless of how each dataset
 * otherwise resolves lots). Never applied when `knownFarmIds` is empty
 * (farms not loaded yet) to avoid a false-positive flash on first render.
 */
export const decorateRowsWithRemovedFarmLotLabel = <T extends Record<string, unknown>>(
  rows: readonly T[],
  knownFarmIds: ReadonlySet<string>,
  suffix: string
): T[] => {
  if (knownFarmIds.size === 0) return rows as T[]

  return rows.map((row) => {
    const farmId = typeof row.farmId === 'string' ? row.farmId.trim() : ''
    if (!farmId || knownFarmIds.has(farmId)) return row

    let changed = false
    const next: Record<string, unknown> = { ...row }
    for (const key of ['lot', 'lotKey']) {
      const value = next[key]
      if (typeof value === 'string' && value.trim().length > 0) {
        next[key] = `${value} ${suffix}`
        changed = true
      }
    }
    return changed ? (next as T) : row
  })
}
